import { useEffect, useRef, useState } from 'react';
import { BeaconJoinCoordinator, type BeaconJoinState, type BeaconJoinPhase } from './BeaconJoinCoordinator';

interface UseBeaconFlowResult {
  state: BeaconJoinState;
  coordinator: BeaconJoinCoordinator | null;
  isDebugMode: boolean;
}

export function useBeaconFlow(shareCode: string): UseBeaconFlowResult {
  const coordinatorRef = useRef<BeaconJoinCoordinator | null>(null);
  const [state, setState] = useState<BeaconJoinState | null>(null);

  const params = new URLSearchParams(window.location.search);
  const isDebugMode = params.has('debug');
  const debugPhase = params.get('debugPhase') as BeaconJoinPhase | undefined;

  useEffect(() => {
    const coordinator = new BeaconJoinCoordinator(shareCode, debugPhase || undefined);
    coordinatorRef.current = coordinator;

    const stateStore = coordinator.getState();
    setState(stateStore.currentState);

    const listenerId = stateStore.addListener((newState) => {
      setState({ ...newState });
    });

    coordinator.init();

    return () => {
      stateStore.removeListener(listenerId);
      coordinator.cleanup();
      coordinatorRef.current = null;
    };
  }, [shareCode]);

  const defaultState: BeaconJoinState = {
    status: 'idle',
    phase: 'page_viewed',
    shareCode,
    sessionId: '',
    isReconnecting: false,
    connectionRetries: 0,
  };

  return {
    state: state ?? defaultState,
    coordinator: coordinatorRef.current,
    isDebugMode,
  };
}
